import React from "react"
import {
  InputAdornment,
  InputBase,
  type InputBaseProps,
  MenuItem,
  Select,
  TextField,
  type TextFieldProps,
  useTheme
} from "@carrotfertility/carotene-core"
import { useController, type ExtendControllerProps, type RequireNameProp } from "@carrotfertility/carotene-core-x"
import { getCountryCallingCode } from "libphonenumber-js"
import { useIntl } from "react-intl"
import { isCountryCode } from "./CountryCode"
import { usePhoneCodeSelectOptions } from "./phoneNumbers"

type FormPhoneNumberFieldProps = TextFieldProps &
  ExtendControllerProps &
  RequireNameProp & {
    // Holds one of `CountryCodes`. None of the AQ, UM or XX edge cases is offered, so every pick
    // converts to E.164.
    countryCodeFieldName: string
    countryCodeLabel: string
  }

// Carotene's theme gives every Select a `notched` prop, which only an outlined input consumes. A
// plain input would put it on the DOM.
const CountryCodeInput = React.forwardRef<HTMLDivElement, InputBaseProps & { notched?: boolean }>(
  function CountryCodeInput({ notched: _notched, ...props }, ref) {
    return <InputBase {...props} ref={ref} />
  }
)

// A phone number field with its country code select inside it. Closed, the select shows only the
// calling code; open, each country's name with its code.
//
// Controlled rather than a `FormTextField`: react-hook-form reads a registered field from the first
// input inside the element its ref lands on, and `FormTextField` puts that ref on the field's root,
// where the first input is now the select's hidden one.
export function FormPhoneNumberField({
  name,
  controllerProps,
  countryCodeFieldName,
  countryCodeLabel,
  helperText,
  placeholder,
  disabled,
  InputProps,
  ...props
}: FormPhoneNumberFieldProps): JSX.Element {
  const intl = useIntl()
  const options = usePhoneCodeSelectOptions().filter((option) => isCountryCode(option.value))
  const {
    field: phone,
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ ...controllerProps, name })
  // `deps` re-checks the number when the country changes, since its validity depends on both.
  const { field: country } = useController({ name: countryCodeFieldName, rules: { deps: [name] } })
  const theme = useTheme()
  // Select centres its menu on the select, and this one is too narrow for that: the menu would hang
  // past the field. It opens under the whole field, from its start edge, instead.
  const [fieldRoot, setFieldRoot] = React.useState<HTMLDivElement | null>(null)
  const menuStart = theme.direction === "rtl" ? "right" : "left"

  return (
    <TextField
      {...props}
      id={name}
      name={phone.name}
      value={phone.value ?? ""}
      onChange={phone.onChange}
      onBlur={phone.onBlur}
      inputRef={phone.ref}
      type="tel"
      placeholder={placeholder ?? ""}
      disabled={isSubmitting || disabled}
      required={Boolean(controllerProps?.rules?.required)}
      error={Boolean(error)}
      helperText={error?.message ?? helperText}
      InputProps={{
        ...InputProps,
        ref: setFieldRoot,
        startAdornment: (
          <InputAdornment position="start">
            <Select<string>
              value={country.value ?? ""}
              onChange={country.onChange}
              onBlur={country.onBlur}
              inputRef={country.ref}
              disabled={isSubmitting || disabled}
              input={<CountryCodeInput />}
              inputProps={{ "aria-label": countryCodeLabel }}
              renderValue={(value) =>
                isCountryCode(value)
                  ? intl.formatMessage({ defaultMessage: "+{phoneCode}" }, { phoneCode: getCountryCallingCode(value) })
                  : ""
              }
              MenuProps={{
                anchorEl: fieldRoot,
                anchorOrigin: { vertical: "bottom", horizontal: menuStart },
                transformOrigin: { vertical: "top", horizontal: menuStart },
                // Select names its listbox only from `labelId`, which this select has no element for.
                MenuListProps: { "aria-label": countryCodeLabel }
              }}
              // Inline because carotene's outlined select padding reaches this select through the text
              // field's own outlined root, with a selector `sx` can't outrank. Its end padding stays: it
              // is the room for the chevron.
              SelectDisplayProps={{ style: { paddingInlineStart: 0 } }}
              sx={(theme) => ({
                borderInlineEnd: `${theme.tokens.borderWidth.md} solid ${theme.palette.border.light}`,
                // Carotene rings only the outlined root when its own input is focused, so this select
                // would otherwise take keyboard focus with nothing to show for it.
                "& .MuiSelect-select:focus-visible": {
                  outline: `${theme.tokens.borderWidth.md} solid ${theme.palette.focusRing}`,
                  outlineOffset: theme.spacing(theme.tokens.spacing.xxxs)
                }
              })}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </InputAdornment>
        )
      }}
    />
  )
}
